import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { EnvironmentService } from '../../../shared/services/environment.service';
import { MockChartData } from '../../../mocks/mock-chart-data';
import { of, firstValueFrom, map, catchError, finalize } from 'rxjs';
import { NotificationService } from '../../../shared/services/notification.service';


export interface ChartDataItem {
  Id: number;
  Label: string;
  Value: number;
  Color: string;
  Percentage?: number;
  Target: number;
  Variance: number;
  Trend: string;
  DisplayOrder: number;
}

export interface ChartConfig {
  Category: string;
  ChartType: string;
  Title: string;
  Description: string;
  DisplayOrder: number;
  ShowLegend: boolean;
  ShowPercentages: boolean;
  ValueSuffix: string;
  ColorScheme: string;
  Data: ChartDataItem[];
}

@Component({
  selector: 'app-chart-detail',
  standalone: true,
  imports: [CommonModule,
    TranslateModule,
    MatIconModule
  ],
  templateUrl: './chartDetailPage.component.html',
  styleUrls: ['./chartDetailPage.component.less']
})
export class ChartDetailPageComponent implements OnInit {
  private _route = inject(ActivatedRoute);
  private _http = inject(HttpClient);
  private _environment = inject(EnvironmentService);
  private _notifService = inject(NotificationService);
  chartConfigs: ChartConfig[] = [];
  notificationInfo: any = {};
  loading = signal(true);
  error = '';

  ngOnInit(): void {
    this.loadChartData();
  }

  private async loadChartData(): Promise<void> {
    const notificationId = this._route.snapshot.paramMap.get('id');

    if (!notificationId) {
      this.error = 'No notification ID provided';
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.error = '';
    console.log('[ChartComponent] Loading data for notification:', notificationId);

    await this._notifService.markNotificationAsRead(notificationId);

    const source$ = this._environment.isDesignMode
      ? of(MockChartData.getChartDetail(notificationId))
      : this._http
        .get(`/api/notifications/${notificationId}/data`, { responseType: 'text' })
        .pipe(
          map(respText => {
            console.log('[ChartComponent] Raw response:', respText);
            return typeof respText === 'string' ? JSON.parse(respText) : respText;
          })
        );

    const configs$ = source$.pipe(
      map((resp: any) => {
        console.log('[ChartComponent] Parsed response:', resp);

        if (!resp?.success) {
          const msg = resp?.message || 'Failed to load data';
          throw new Error(msg);
        }

        this.notificationInfo = {
          title: resp.title,
          body: resp.body,
          type: resp.type,
          created: resp.created
        };

        const raw = typeof resp.data === 'string' ? JSON.parse(resp.data) : resp.data;
        const list = Array.isArray(raw) ? raw : [];

        console.log('[ChartComponent] Chart configs (raw):', list);

        const configs = list.map((cfg: any) => ({
          ...cfg,
          Data: typeof cfg?.Data === 'string' ? JSON.parse(cfg.Data) : cfg?.Data
        }));

        configs.sort((a: any, b: any) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0));
        return configs as ChartConfig[];
      }),
      catchError(err => {
        console.error('[ChartComponent] Error loading charts:', err);
        this.error = err instanceof Error ? err.message : 'Error loading chart data. Please try again.';
        return of([] as ChartConfig[]);
      }),
      finalize(() => {
        this.loading.set(false);
      })
    );

    this.chartConfigs = await firstValueFrom(configs$);
    console.log('[ChartComponent] Chart configs (normalized):', this.chartConfigs);
  }

  formatValue(value: number, suffix: string = ''): string {
    if (value >= 1000) {
      return (value / 1000).toFixed(1) + 'k' + suffix;
    }
    return value.toLocaleString() + suffix;
  }

  formatPercentage(percentage: number): string {
    if (!percentage) {
      return '';
    }
    return percentage.toFixed(1) + '%';
  }

  getVarianceClass(variance: number): string {
    if (!variance) return '';
    if (variance > 0) return 'variance-positive';
    if (variance < 0) return 'variance-negative';
    return 'variance-neutral';
  }

  getTrendClass(trend: string): string {
    switch (trend?.toLowerCase()) {
      case 'above': return 'trend-up';
      case 'below': return 'trend-down';
      default: return 'trend-neutral';
    }
  }

  getTrendLabel(trend: string, chartType: string = ''): string {
    const isAbove = trend?.toLowerCase() === 'above';

    switch (chartType) {
      case 'line':
        return isAbove ? 'CHARTS.ABOVE' : 'CHARTS.BELOW';
      case 'summary':
        return isAbove ? 'CHARTS.SUCCESS' : 'CHARTS.FAIL';
      default:
        return isAbove ? 'trending_up' : 'trending_down';
    }
  }

  getMaxValue(data: ChartDataItem[]): number {
    return Math.max(...data.map(item => item.Value || 0));
  }

  getAbsoluteValue(value: number): number {
    return Math.abs(value);
  }

  trackByChartId(index: number, item: ChartDataItem): number {
    return item.Id || index;
  }

  trackByCategory(index: number, config: ChartConfig): string {
    return config.Category;
  }
}